import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Calendar as CalendarIcon, MapPin, Tag, Sparkles, Phone, MessageCircle, ArrowRight, Anchor, DollarSign, ShieldCheck, Compass, Ship as ShipIcon, Award, ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { format } from 'date-fns';
import SectionTitle from '../components/SectionTitle';
import { CruiseCard, PackageCard } from '../components/Cards';
import { useSettings } from '../context/SettingsContext';
import { publicApi } from '../api/client';
import { toast } from '../hooks/use-toast';

const DURATIONS = ['3 Nights From Aswan', '4 Nights From Luxor', '7 Nights Long Cruise', 'Egypt Tour Package'];
const CATEGORIES = ['All', 'Luxury Nile', 'Deluxe Nile River', 'Standard Nile River', 'Dahabiya Nile'];

const Home = () => {
  const settings = useSettings();
  const nav = useNavigate();
  const [heroIdx, setHeroIdx] = useState(0);
  const [duration, setDuration] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState();

  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [packages, setPackages] = useState([]);
  const [tours, setTours] = useState([]);
  const [blog, setBlog] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [popIdx, setPopIdx] = useState(0);

  useEffect(() => {
    publicApi.getCruises({ featured: true }).then(setFeatured).catch(() => {});
    publicApi.getCruises({ popular: true }).then(setPopular).catch(() => {});
    publicApi.getPackages({ featured: true }).then(setPackages).catch(() => {});
    publicApi.getTours().then(setTours).catch(() => {});
    publicApi.getBlog().then((d) => setBlog(d.slice(0, 6))).catch(() => {});
    publicApi.getFaqs().then(setFaqs).catch(() => {});
  }, []);

  const heroImages = settings.hero_images && settings.hero_images.length > 0 ? settings.hero_images : ['https://images.unsplash.com/photo-1575501376367-6711b9b5756b?crop=entropy&cs=srgb&fm=jpg&q=85'];

  useEffect(() => {
    const i = setInterval(() => setHeroIdx((v) => (v + 1) % heroImages.length), 6000);
    return () => clearInterval(i);
  }, [heroImages.length]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (category && category !== 'All') params.set('category', category);
    toast({ title: 'Searching your perfect journey', description: `${duration || 'Any duration'} · ${category || 'All categories'} · ${date ? format(date, 'PPP') : 'flexible dates'}` });
    nav(`/cruises${params.toString() ? '?' + params.toString() : ''}`);
  };

  const perPage = 3;
  const visiblePopular = popular.length > 0 ? Array.from({ length: Math.min(perPage, popular.length) }).map((_, k) => popular[(popIdx + k) % popular.length]) : [];

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        {heroImages.map((src, i) => (
          <div key={src + i} className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${heroIdx === i ? 'opacity-100' : 'opacity-0'}`} style={{ backgroundImage: `url(${src})` }} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-[#304b71]/70 via-[#304b71]/40 to-[#304b71]/80" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.15),transparent_60%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#a6b2b5] animate-pulse" />
              <span className="text-white/90 text-xs uppercase tracking-[0.25em]">Exclusive Egypt Journeys</span>
            </div>
            <h1 className="font-serif text-white text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">{settings.hero_title}</h1>
            <p className="mt-6 text-white/85 text-lg md:text-xl max-w-2xl leading-relaxed">{settings.hero_subtitle}</p>
          </div>

          <div className="mt-12 bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-4 md:p-6 max-w-5xl border border-white/40">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-xl">
                <MapPin size={18} className="text-[#6a8aa4]" />
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="border-0 shadow-none focus:ring-0 h-11 p-0 text-slate-700"><SelectValue placeholder="Duration & Route" /></SelectTrigger>
                  <SelectContent>{DURATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-xl">
                <CalendarIcon size={18} className="text-[#6a8aa4]" />
                <Popover>
                  <PopoverTrigger asChild><button className="flex-1 text-left h-11 text-slate-700 text-sm">{date ? format(date, 'PPP') : <span className="text-slate-400">Pick a date</span>}</button></PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={date} onSelect={setDate} initialFocus /></PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-xl">
                <Tag size={18} className="text-[#6a8aa4]" />
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="border-0 shadow-none focus:ring-0 h-11 p-0 text-slate-700"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={handleSearch} className="h-14 bg-[#304b71] hover:bg-[#6a8aa4] text-white rounded-xl text-base font-semibold">
                <Search size={18} className="mr-2" /> Search
              </Button>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-white/80 text-sm">
            <span>✦ 200+ Nile Cruises</span><span>✦ 25 Years of Expertise</span><span>✦ Free Cabin Upgrades</span><span>✦ Best Price Guaranteed</span>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {heroImages.map((_, i) => <button key={i} onClick={() => setHeroIdx(i)} className={`h-1.5 rounded-full transition-all ${i === heroIdx ? 'w-10 bg-white' : 'w-4 bg-white/40'}`} />)}
        </div>
      </section>

      {/* TOP RATED */}
      <section className="py-24 bg-[#f6f8fa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <SectionTitle eyebrow="Top Rated" title="Top-Rated Nile Cruises in Egypt" subtitle="Egypt's best-loved river ships, hand-picked based on real customer reviews. From five-star luxury sailings on Soleil and Historia to intimate Dahabiya voyages — all include the legendary 230 km sail between Luxor and Aswan." />
            <Link to="/cruises" className="text-[#304b71] hover:text-[#6a8aa4] font-semibold whitespace-nowrap inline-flex items-center gap-1 pb-4">Discover more <ArrowRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((c) => <CruiseCard key={c.id} cruise={c} />)}
          </div>
        </div>
      </section>

      {/* HELP CHOOSE */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img src="https://images.unsplash.com/photo-1774284498049-eee34f796ce7?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Luxury cruise" className="w-full h-72 object-cover rounded-2xl shadow-lg translate-y-8" />
                <img src="https://images.unsplash.com/photo-1568503446072-5c1c17d9b018?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Egypt experts" className="w-full h-72 object-cover rounded-2xl shadow-lg" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#304b71] text-white p-6 rounded-2xl shadow-2xl max-w-xs">
                <div className="font-serif text-3xl">25+</div>
                <div className="text-white/80 text-sm mt-1">Years crafting unforgettable Nile journeys</div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 text-[#6a8aa4] uppercase tracking-[0.25em] text-xs mb-4"><Sparkles size={14} /> Personal Concierge</div>
              <h2 className="font-serif text-[#304b71] text-3xl md:text-5xl leading-tight">Need help choosing your <span className="italic">Egypt Nile Cruise?</span></h2>
              <p className="mt-6 text-slate-600 leading-relaxed">With so many Nile ships and itineraries, choosing the right one can feel overwhelming. Our dedicated Egypt cruise specialists will guide you — sharing insider tips, exclusive rates and personalized advice so you book the perfect journey with confidence.</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button asChild className="bg-[#304b71] hover:bg-[#6a8aa4] text-white rounded-full px-6 h-12"><a href={`tel:${settings.phone_raw}`}><Phone size={16} className="mr-2" /> Call Your Cruise Expert</a></Button>
                <Button asChild variant="outline" className="border-[#304b71] text-[#304b71] hover:bg-[#304b71] hover:text-white rounded-full px-6 h-12"><Link to="/request-quote"><MessageCircle size={16} className="mr-2" /> Request a Quote</Link></Button>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4">
                {[{ n: '15k+', l: 'Happy Guests' }, { n: '200+', l: 'Cruise Ships' }, { n: '4.9', l: 'Guest Rating' }].map((s) => (
                  <div key={s.l} className="border-l-2 border-[#a6b2b5] pl-4"><div className="font-serif text-2xl text-[#304b71]">{s.n}</div><div className="text-xs text-slate-500 uppercase tracking-wider">{s.l}</div></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR CRUISES */}
      {popular.length > 0 && (
        <section className="py-24 bg-[#f6f8fa]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <SectionTitle eyebrow="Most Popular" title="Most Popular Nile River Cruises" subtitle="The Nile ships our travellers rave about — from panoramic 5-star sailings to intimate Dahabiya adventures." />
              <div className="flex items-center gap-3 pb-4">
                <button onClick={() => setPopIdx((v) => (v - 1 + popular.length) % popular.length)} className="w-11 h-11 rounded-full border border-[#304b71] text-[#304b71] hover:bg-[#304b71] hover:text-white transition-colors flex items-center justify-center"><ChevronLeft size={18} /></button>
                <button onClick={() => setPopIdx((v) => (v + 1) % popular.length)} className="w-11 h-11 rounded-full border border-[#304b71] text-[#304b71] hover:bg-[#304b71] hover:text-white transition-colors flex items-center justify-center"><ChevronRight size={18} /></button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {visiblePopular.map((c, i) => (
                <Link key={c.id + '-' + i} to={`/cruises/${c.slug}`} className="group relative overflow-hidden rounded-2xl h-[440px] bg-black shadow-lg block">
                  <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#304b71] via-[#304b71]/40 to-transparent" />
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur border border-white/30 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#304b71] transition"><ArrowUpRight size={16} /></div>
                  <div className="absolute bottom-0 left-0 right-0 p-7 text-white">
                    <h3 className="font-serif text-2xl mb-2 leading-snug">{c.name}</h3>
                    <p className="text-white/85 text-sm leading-relaxed line-clamp-3">{c.short_desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PACKAGES */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <SectionTitle eyebrow="Curated Journeys" title="Best Egypt Tours, Nile Cruise Packages & Deals" subtitle="Culturally immersive journeys along Africa's longest river — crafted for modern travellers who value luxury, relaxation and timeless discovery. Each package includes hotels, meals, guided excursions and domestic transfers." />
            <Link to="/packages" className="text-[#304b71] hover:text-[#6a8aa4] font-semibold whitespace-nowrap inline-flex items-center gap-1 pb-4">Explore further <ArrowRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((p) => <PackageCard key={p.id} pkg={p} />)}
          </div>
        </div>
      </section>

      {/* TOURS */}
      {tours.length > 0 && (
        <section className="py-24 bg-gradient-to-b from-[#f6f8fa] to-white">
          <div className="max-w-7xl mx-auto px-6">
            <SectionTitle eyebrow="Explore Egypt" title="Top Tours & Nile Cruise Packages" subtitle="From the eternal Pyramids of Giza and Coptic Cairo to the Red Sea and the golden desert of Siwa — combine Egypt's icons with a boutique Nile sailing experience for every travel style." />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tours.map((t, idx) => (
                <Link key={t.id} to={`/packages`} className={`group relative overflow-hidden rounded-2xl bg-black shadow-md hover:shadow-2xl transition-all duration-500 ${idx === 0 ? 'lg:col-span-2 aspect-[16/9] lg:aspect-auto lg:h-[380px]' : 'aspect-[4/3]'}`}>
                  <img src={t.image} alt={t.name} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#304b71]/95 via-[#304b71]/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between text-white">
                    <h3 className="font-serif text-2xl md:text-3xl leading-tight max-w-xs">{t.name}</h3>
                    <div className="w-12 h-12 rounded-full bg-white/15 border border-white/30 backdrop-blur flex items-center justify-center group-hover:bg-white group-hover:text-[#304b71] transition-colors"><ArrowUpRight size={18} /></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY CHOOSE US */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#304b71]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(166,178,181,0.25),transparent_50%)]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1643806294235-3ee68b3bc9d2?crop=entropy&cs=srgb&fm=jpg&q=85')", backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[#a6b2b5] uppercase tracking-[0.25em] text-xs mb-4"><span className="w-8 h-[1px] bg-[#a6b2b5]" /> Why Choose Us</div>
            <h2 className="font-serif text-white text-3xl md:text-5xl leading-tight">Why choose {settings.brand} for your <span className="italic text-[#a6b2b5]">Egypt River Cruise?</span></h2>
            <p className="mt-5 text-white/75 leading-relaxed">We match every traveller with the perfect Nile ship. With expert recommendations, insider access and 25 years of specialization, we make it effortless to book a river cruise that fits your style and budget.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {[
              { icon: Anchor, title: "Egypt's largest Nile Cruise portfolio", desc: 'We list every Nile cruise in Egypt — standard, deluxe, luxury and Dahabiya — with authentic photography, honest reviews and prices for every budget.' },
              { icon: DollarSign, title: 'Real, unbeatable prices', desc: 'As official cruise supplier, we guarantee the lowest published rates — no post-booking surprises. Taxes, fees and onboard programs included.' },
              { icon: ShieldCheck, title: 'Easy booking, secured payment', desc: 'Send an inquiry, get a fast expert reply, and pay securely through internationally-audited payment gateways.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div key={title} className="group bg-white/8 hover:bg-white backdrop-blur border border-white/15 hover:border-white rounded-2xl p-8 transition-all duration-500">
                <div className="w-14 h-14 rounded-full bg-[#a6b2b5]/20 group-hover:bg-[#304b71] text-white flex items-center justify-center mb-6 transition-colors"><Icon size={22} /></div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#a6b2b5] group-hover:text-[#6a8aa4] mb-2">0{i + 1}</div>
                <h3 className="font-serif text-xl text-white group-hover:text-[#304b71] leading-snug transition-colors">{title}</h3>
                <p className="mt-4 text-white/70 group-hover:text-slate-600 text-sm leading-relaxed transition-colors">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXCLUSIVE TOURS CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1559738933-d69ac3ff674b?crop=entropy&cs=srgb&fm=jpg&q=85')" }} />
        <div className="absolute inset-0 bg-[#304b71]/85" />
        <div className="relative max-w-7xl mx-auto px-6 text-center text-white">
          <div className="inline-flex items-center gap-2 text-[#a6b2b5] uppercase tracking-[0.35em] text-xs mb-4 justify-center"><span className="w-8 h-[1px] bg-[#a6b2b5]" /> Exclusive Egypt Tours <span className="w-8 h-[1px] bg-[#a6b2b5]" /></div>
          <h2 className="font-serif text-4xl md:text-6xl leading-tight max-w-3xl mx-auto">Discover the Wonders of <span className="italic text-[#a6b2b5]">Egypt</span></h2>
          <p className="mt-5 text-white/80 max-w-2xl mx-auto">Enjoy complimentary cabin upgrades and world-class service when you book your Egypt tour with {settings.brand}.</p>
          <div className="grid md:grid-cols-3 gap-6 mt-16 text-left">
            {[
              { icon: Compass, title: 'Expert Egyptologists', desc: 'Discover the hidden meanings of ancient temples and tombs with your own private Egyptologist guide.' },
              { icon: ShipIcon, title: 'Serene Nile Cruising', desc: 'Relax aboard elegant sailing vessels and take in the majestic timeless landscapes along the river.' },
              { icon: Award, title: 'Unmatched Value', desc: 'Book now to unlock exclusive itinerary upgrades and seasonal savings on your grand Egypt tour.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="border border-white/15 bg-white/5 backdrop-blur-sm p-8 rounded-2xl hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 border border-[#a6b2b5]/40 rounded-full flex items-center justify-center text-[#a6b2b5] mb-5"><Icon size={20} /></div>
                <h3 className="font-serif text-xl mb-3">{title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-14">
            <Button asChild size="lg" className="bg-white text-[#304b71] hover:bg-[#a6b2b5] hover:text-white rounded-full px-8 h-14 text-base font-semibold"><Link to="/packages">Save Big on Egypt Tours <ArrowRight size={16} className="ml-2" /></Link></Button>
          </div>
        </div>
      </section>

      {/* BLOG */}
      {blog.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <SectionTitle eyebrow="Travel Guide" title="Egypt Tour & Nile Cruise Travel Guide" subtitle="Expert tips, planning guides and cultural insights — everything you need to know before you sail the Nile." />
              <Link to="/blog" className="text-[#304b71] hover:text-[#6a8aa4] font-semibold whitespace-nowrap inline-flex items-center gap-1 pb-4">View more <ArrowRight size={16} /></Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blog.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                  <div className="aspect-[16/10] overflow-hidden rounded-2xl mb-5">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[#6a8aa4] mb-2">Travel Guide</div>
                  <h3 className="font-serif text-xl text-[#304b71] leading-snug group-hover:text-[#6a8aa4] transition-colors">{post.title}</h3>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-[#304b71] text-sm font-semibold group-hover:gap-2 transition-all">Read more <ArrowRight size={14} /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-24 bg-[#f6f8fa]">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 text-[#6a8aa4] uppercase tracking-[0.25em] text-xs mb-4 justify-center"><span className="w-8 h-[1px] bg-[#6a8aa4]" /> FAQ <span className="w-8 h-[1px] bg-[#6a8aa4]" /></div>
              <h2 className="font-serif text-[#304b71] text-3xl md:text-5xl leading-tight">Frequently Asked Questions</h2>
              <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Everything you need to know before booking your Nile Cruise in Egypt.</p>
            </div>
            <div className="mt-14">
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((f) => (
                  <AccordionItem key={f.id} value={f.id} className="border border-slate-200 bg-white rounded-xl px-5 data-[state=open]:border-[#6a8aa4]">
                    <AccordionTrigger className="font-serif text-left text-[#304b71] text-lg hover:no-underline py-5">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-slate-600 leading-relaxed pb-5">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Home;
